import { useState } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { useProfile } from "@/hooks/useProfile";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Badge } from "@/components/ui/badge";
import { Edit, User, Mail, Calendar, HardDrive, LogOut, Settings, Globe, Gift } from "lucide-react";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { EditProfileModal } from "@/components/profile/EditProfileModal";
import { NotificationSettings } from "@/components/profile/NotificationSettings";
import StorageWidget from "@/components/profile/StorageWidget";
import SubscriptionPlansOverview from "@/components/profile/SubscriptionPlansOverview";
import { HandoverTokenInterface } from "@/components/profile/HandoverTokenInterface";
import { AccountDangerZone } from "@/components/profile/AccountDangerZone";
import LanguageSwitcher from "@/components/LanguageSwitcher";
import { useTranslation } from "react-i18next";
import AppShell from "@/components/AppShell";
import { toast } from "sonner";

const ProfilePage = () => {
  const { user, signOut } = useAuth();
  const { profile, loading, updateProfile, updateEmail } = useProfile();
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<'profile' | 'settings' | 'storage' | 'plans' | 'handover'>('profile');
  const { t } = useTranslation('profile');

  const handleLogout = async () => {
    try {
      await signOut();
      // Redirect to landing page after logout
      window.location.href = 'https://www.uplivion.life';
    } catch (error) {
      console.error('Logout error:', error);
      toast.error('Fehler beim Abmelden');
    }
  };

  if (loading) {
    return (
      <AppShell showHeader={false} backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)">
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-uplivion-blue"></div>
        </div>
      </AppShell>
    );
  }

  if (!user || !profile) {
    return (
      <AppShell showHeader={false} backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)">
        <div className="min-h-screen flex items-center justify-center">
          <Card className="w-full max-w-md uplivion-card">
            <CardContent className="pt-6 text-center">
              <p className="text-muted-foreground">{t('error.notFound', 'Profil nicht gefunden')}</p>
            </CardContent>
          </Card>
        </div>
      </AppShell>
    );
  }

  const getInitials = () => {
    if (profile.first_name && profile.last_name) {
      return `${profile.first_name[0]}${profile.last_name[0]}`.toUpperCase();
    }
    if (profile.username) {
      return profile.username.slice(0, 2).toUpperCase();
    }
    return "?";
  };

  const tabs = [
    { id: 'profile' as const, label: t('tabs.profile', 'Profil'), icon: User },
    { id: 'settings' as const, label: t('tabs.settings', 'Einstellungen'), icon: Settings },
    { id: 'storage' as const, label: t('tabs.storage', 'Speicher'), icon: HardDrive },
    { id: 'plans' as const, label: t('tabs.plans', 'Pläne'), icon: Calendar },
    { id: 'handover' as const, label: 'Timeline Handover', icon: Gift }
  ];

  return (
    <AppShell showHeader={false} backgroundGradient="linear-gradient(154deg, #9edfff 2%, rgb(191, 234, 255) 26%, rgb(212, 204, 255) 100%)">
      <div className="container mx-auto py-8 px-4">
        <div className="max-w-4xl mx-auto space-y-6">
          {/* Header */}
          <Card className="uplivion-card">
            <CardHeader>
              <div className="flex flex-col sm:flex-row items-start sm:items-center gap-4">
                <Avatar className="w-20 h-20">
                  <AvatarImage src="" alt={profile.username} />
                  <AvatarFallback className="text-2xl bg-uplivion-blue text-white">
                    {getInitials()}
                  </AvatarFallback>
                </Avatar>
                
                <div className="flex-1">
                  <div className="flex flex-col sm:flex-row sm:items-center gap-2 mb-2">
                    <h1 className="text-2xl font-bold">
                      {profile.first_name && profile.last_name 
                        ? `${profile.first_name} ${profile.last_name}`
                        : profile.username
                      }
                    </h1>
                    <Badge variant="secondary">@{profile.username}</Badge>
                  </div>
                  
                  <div className="flex flex-col sm:flex-row gap-4 text-sm text-muted-foreground">
                    <span>{user.email}</span>
                    {profile.birthdate && (
                      <span>
                        {format(profile.birthdate, "dd.MM.yyyy", { locale: de })}
                      </span>
                    )}
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-2">
                  <Button onClick={() => setIsEditModalOpen(true)} className="gap-2">
                    <Edit className="w-4 h-4" />
                    {t('edit.button', 'Bearbeiten')}
                  </Button>
                  <Button 
                    onClick={handleLogout} 
                    variant="outline" 
                    className="gap-2 text-red-600 hover:text-red-700 border-red-200 hover:border-red-300"
                  >
                    <LogOut className="w-4 h-4" />
                    {t('logout.button', 'Abmelden')}
                  </Button>
                </div>
              </div>
            </CardHeader>
          </Card>

          {/* Navigation Tabs */}
          <div className="uplivion-card rounded-lg p-1">
            <div className="flex flex-wrap gap-2">
              {tabs.map((tab) => {
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md transition-colors ${
                      activeTab === tab.id
                        ? 'bg-white/80 text-uplivion-blue shadow-sm backdrop-blur-sm'
                        : 'text-gray-600 hover:text-uplivion-blue hover:bg-white/40'
                      }`}
                  >
                    <Icon className="w-4 h-4" />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'profile' && (
              <div>
                <Card className="uplivion-card">
                  <CardHeader>
                    <CardTitle>{t('profile.title', 'Profil-Informationen')}</CardTitle>
                    <CardDescription>
                      {t('profile.description', 'Verwalten Sie Ihre persönlichen Informationen')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('profile.firstName', 'Vorname')}
                        </label>
                        <p className="mt-1">{profile.first_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('profile.lastName', 'Nachname')}
                        </label>
                        <p className="mt-1">{profile.last_name || '-'}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('profile.username', 'Benutzername')}
                        </label>
                        <p className="mt-1">@{profile.username}</p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">
                          {t('profile.birthdate', 'Geburtsdatum')}
                        </label>
                        <p className="mt-1">
                          {profile.birthdate 
                            ? format(profile.birthdate, "dd.MM.yyyy", { locale: de })
                            : '-'
                          }
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}

            {activeTab === 'settings' && (
              <div className="space-y-6">
                <NotificationSettings />
                
                <Card className="uplivion-card">
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Globe className="w-5 h-5" />
                      {t('generalSettings', 'Allgemeine Einstellungen')}
                    </CardTitle>
                    <CardDescription>
                      {t('generalSettingsDescription', 'Verwalten Sie Ihre allgemeinen Einstellungen')}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="flex items-center justify-between">
                      <div>
                        <h3 className="font-medium">{t('language', 'Sprache')}</h3>
                        <p className="text-sm text-muted-foreground">
                          {t('languageDescription', 'Wählen Sie Ihre bevorzugte Sprache')}
                        </p>
                      </div>
                       <LanguageSwitcher />
                    </div>
                  </CardContent>
                </Card>
                
                <AccountDangerZone />
              </div>
            )}

            {activeTab === 'storage' && (
              <div className="space-y-6">
                <StorageWidget showUpgradeButton={true} compact={false} />
              </div>
            )}

            {activeTab === 'plans' && (
              <div>
                <SubscriptionPlansOverview />
              </div>
            )}

            {activeTab === 'handover' && (
              <div>
                <HandoverTokenInterface />
              </div>
            )}
          </div>
        </div>

        <EditProfileModal
          isOpen={isEditModalOpen}
          onClose={() => setIsEditModalOpen(false)}
          profile={profile}
          userEmail={user.email || ''}
          onUpdateProfile={updateProfile}
          onUpdateEmail={updateEmail}
        />
      </div>
    </AppShell>
  );
};

export default ProfilePage;
